import { useState, useEffect } from 'react';
import { X, Phone, Mail, Globe, MapPin, Sparkles, Check, Trash2, RefreshCw, Zap, Clock, Copy, Building, Send, PhoneCall, FileText, Flame, Loader2, AlertTriangle, BookOpen, Sun, Smile, Plus, Star } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { API, SECTEURS, STATUTS, PRIORITES, COMM_TYPES, RESULTATS_APPEL, PROCHAINES_ACTIONS, StatutBadge, ScoreBadge } from './constants';

export function ProspectDetail({ prospect, onClose, onUpdate, onDelete, onRefresh, catalogue }) {
  const [p, setP] = useState(prospect);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('info');
  const [enriching, setEnriching] = useState(false);
  const [enrichment, setEnrichment] = useState(p.enrichment || null);
  const [sectorScript, setSectorScript] = useState(null);
  const [loadingScript, setLoadingScript] = useState(false);
  const [showCommForm, setShowCommForm] = useState(false);
  const [commForm, setCommForm] = useState({ type: 'appel', sujet: '', contenu: '', resultat: '', montantEstime: '', prochaineAction: '' });
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', sender: 'restaurant' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [emailType, setEmailType] = useState('premier_contact');
  const [emailContext, setEmailContext] = useState('');
  const [offresRecommandees, setOffresRecommandees] = useState([]);
  const [emailVersions, setEmailVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [saisonHook, setSaisonHook] = useState('');
  const [siteScraped, setSiteScraped] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleSuggestion, setScheduleSuggestion] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [creatingSequence, setCreatingSequence] = useState(false);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiSuggestionLoading, setAiSuggestionLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSuggestion = async () => {
      setAiSuggestionLoading(true);
      try {
        const res = await fetch(`${API}/api/crm/prospects/${p.id}/suggestion-ia`, { method: 'POST' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setAiSuggestion(data);
        }
      } catch { /* silent */ }
      if (!cancelled) setAiSuggestionLoading(false);
    };
    fetchSuggestion();
    return () => { cancelled = true; };
  }, [p.id]);

  useEffect(() => {
    if (activeSection === 'ia' && !sectorScript && !loadingScript) {
      setLoadingScript(true);
      fetch(`${API}/api/crm/scripts/${p.secteur || 'entreprise'}`)
        .then(r => r.json())
        .then(data => { setSectorScript(data); setLoadingScript(false); })
        .catch(() => setLoadingScript(false));
    }
  }, [activeSection, p.secteur, sectorScript, loadingScript]);

  const save = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      const data = await res.json(); setP(data); onUpdate(data); toast.success('Sauvegarde');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await fetch(`${API}/api/crm/prospects/${p.id}/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: noteText }) });
      setP(prev => ({ ...prev, notes: [...(prev.notes || []), { text: noteText, date: new Date().toISOString() }] }));
      setNoteText(''); toast.success('Note ajoutee');
    } catch { toast.error('Erreur'); }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}/enrich`, { method: 'POST' });
      const data = await res.json();
      setEnrichment(data); setP(prev => ({ ...prev, enrichi: true, scoreIA: data.scoreIA, enrichment: data }));
      onUpdate({ ...p, enrichi: true, scoreIA: data.scoreIA, enrichment: data });
      toast.success(`Score IA: ${data.scoreIA}/100`);
    } catch { toast.error('Erreur enrichissement'); }
    setEnriching(false);
  };

  const addComm = async () => {
    if (!commForm.sujet.trim()) return;
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}/communication`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(commForm) });
      const data = await res.json();
      if (data.prospect) { setP(data.prospect); onUpdate(data.prospect); }
      else { setP(prev => ({ ...prev, communications: [...(prev.communications || []), data.communication || data] })); }
      setCommForm({ type: 'appel', sujet: '', contenu: '', resultat: '', montantEstime: '', prochaineAction: '' });
      setShowCommForm(false);
      toast.success('Communication enregistree');
      if (data.prospect && data.prospect.statut !== p.statut) toast.info(`Pipeline mis a jour: ${STATUTS[data.prospect.statut]?.label}`);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Mettre a la corbeille ?')) return;
    try { await fetch(`${API}/api/crm/prospects/${p.id}`, { method: 'DELETE' }); onDelete(p.id); onClose(); toast.success('Mis a la corbeille'); } catch { toast.error('Erreur'); }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Copie !'); };

  const generateEmailIA = async (type = emailType) => {
    setGeneratingEmail(true);
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}/generate-email-ia`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, context: emailContext }),
      });
      const data = await res.json();
      if (res.ok) {
        const versions = data.versions || [];
        setEmailVersions(versions);
        setOffresRecommandees(data.offres_recommandees || []);
        setSaisonHook(data.saison_hook || '');
        setSiteScraped(data.site_scraped || false);
        setSelectedVersion(0);
        if (versions.length > 0) {
          setEmailForm({ subject: versions[0].subject, body: versions[0].body, sender: 'restaurant' });
        }
        setShowEmailForm(true);
        toast.success(`${versions.length} versions generees !${data.site_scraped ? ' (site web analyse)' : ''}`);
      } else { toast.error(data.detail || 'Erreur IA'); }
    } catch { toast.error('Erreur generation'); }
    setGeneratingEmail(false);
  };

  const selectEmailVersion = (idx) => {
    setSelectedVersion(idx);
    const v = emailVersions[idx];
    if (v) setEmailForm(prev => ({ ...prev, subject: v.subject, body: v.body }));
  };

  const sendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) { toast.error('Sujet et contenu requis'); return; }
    if (!p.email) { toast.error('Pas d\'email pour ce prospect'); return; }
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}/send-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: p.email, subject: emailForm.subject, body: emailForm.body, sender: emailForm.sender }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Email envoye a ${p.email}`);
        setShowEmailForm(false);
        setEmailForm({ subject: '', body: '', sender: 'restaurant' });
        onRefresh();
      } else { toast.error(data.detail || 'Erreur envoi'); }
    } catch { toast.error('Erreur envoi email'); }
    setSendingEmail(false);
  };

  const fetchScheduleSuggestion = async () => {
    try {
      const res = await fetch(`${API}/api/crm/email-schedule/suggest/${p.id}`);
      const data = await res.json();
      if (res.ok) setScheduleSuggestion(data);
    } catch {}
  };

  const scheduleEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) { toast.error('Sujet et contenu requis'); return; }
    setScheduling(true);
    try {
      const res = await fetch(`${API}/api/crm/email-schedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospect_id: p.id, subject: emailForm.subject, body: emailForm.body, sender: emailForm.sender, send_at: scheduleSuggestion?.send_at }),
      });
      if (res.ok) {
        toast.success(`Email programme pour ${scheduleSuggestion?.send_at_local || 'bientot'}`);
        setShowSchedule(false);
        setShowEmailForm(false);
        loadScheduledEmails();
      }
    } catch { toast.error('Erreur'); }
    setScheduling(false);
  };

  const createSequence = async () => {
    setCreatingSequence(true);
    try {
      const res = await fetch(`${API}/api/crm/email-sequence`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospect_id: p.id, sender: emailForm.sender || 'restaurant' }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Sequence de ${data.count} emails programmee !`);
        loadScheduledEmails();
      } else { toast.error(data.detail || 'Erreur'); }
    } catch { toast.error('Erreur sequence'); }
    setCreatingSequence(false);
  };

  const loadScheduledEmails = async () => {
    try {
      const res = await fetch(`${API}/api/crm/email-schedule`);
      const data = await res.json();
      setScheduledEmails((data || []).filter(e => e.prospect_id === p.id && e.status === 'programme'));
    } catch {}
  };

  const cancelScheduled = async (emailId) => {
    try {
      await fetch(`${API}/api/crm/email-schedule/${emailId}`, { method: 'DELETE' });
      setScheduledEmails(prev => prev.filter(e => e.id !== emailId));
      toast.success('Email annule');
    } catch { toast.error('Erreur'); }
  };

  useEffect(() => { loadScheduledEmails(); }, [p.id]);

  const SECTIONS = [
    { id: 'info', label: 'Infos', icon: Building },
    { id: 'comms', label: 'Comms', icon: PhoneCall },
    { id: 'email', label: 'Email', icon: Send },
    { id: 'ia', label: 'IA & Scripts', icon: Sparkles },
    { id: 'notes', label: 'Notes', icon: FileText },
    { id: 'history', label: 'Histo', icon: Clock },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-[20px] max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="prospect-detail">
        <div className="border-b px-4 py-3 flex items-center gap-3 shrink-0 bg-gradient-to-r from-[#1B2A52] to-[#243558]">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2"><h2 className="text-base font-bold text-white truncate">{p.nom}</h2><ScoreBadge score={p.scoreIA} /></div>
            <p className="text-xs text-white/60">{p.contact}{p.poste ? ` \u2014 ${p.poste}` : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60"><X size={18} /></button>
        </div>
        <div className="px-4 py-2.5 border-b bg-gray-50/80 overflow-x-auto shrink-0">
          <div className="flex gap-1">
            {Object.entries(STATUTS).map(([k, v]) => (
              <button key={k} onClick={() => save({ statut: k })}
                className={`text-[9px] px-2.5 py-1.5 rounded-full font-bold transition-all whitespace-nowrap ${p.statut === k ? 'text-white shadow-sm scale-105' : 'opacity-40 hover:opacity-70'}`}
                style={{ background: p.statut === k ? v.color : v.color + '15', color: p.statut === k ? '#fff' : v.color }}
                data-testid={`set-statut-${k}`}>{v.label}</button>
            ))}
          </div>
        </div>
        <div className="px-4 py-2 border-b bg-white shrink-0">
          <div className="flex gap-2">
            {p.tel && (
              <a href={`tel:${p.tel}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-50 text-green-600 text-[10px] font-medium hover:bg-green-100 transition-colors" data-testid="quick-call">
                <Phone size={12} /> Appeler
              </a>
            )}
            <button onClick={() => { setActiveSection('email'); generateEmailIA('premier_contact'); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-medium hover:bg-amber-100 transition-colors" data-testid="quick-email-ia">
              <Sparkles size={12} /> Email IA
            </button>
            {p.email && (
              <a href={`mailto:${p.email}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-50 text-blue-600 text-[10px] font-medium hover:bg-blue-100 transition-colors" data-testid="quick-mailto">
                <Mail size={12} /> Email direct
              </a>
            )}
            {p.siteWeb && (
              <a href={p.siteWeb.startsWith('http') ? p.siteWeb : `https://${p.siteWeb}`} target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-purple-50 text-purple-600 text-[10px] font-medium hover:bg-purple-100 transition-colors" data-testid="quick-web">
                <Globe size={12} /> Site
              </a>
            )}
          </div>
        </div>
        <div className="flex border-b shrink-0">
          {SECTIONS.map(s => { const Icon = s.icon; return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-[10px] font-medium border-b-2 transition-colors ${activeSection === s.id ? 'border-[#1B2A52] text-[#1B2A52]' : 'border-transparent text-gray-400'}`}>
              <Icon size={12} />{s.label}</button>
          ); })}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeSection === 'info' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[{ label: 'Email', key: 'email', icon: Mail }, { label: 'Telephone', key: 'tel', icon: Phone }, { label: 'Site web', key: 'siteWeb', icon: Globe }, { label: 'Ville', key: 'ville', icon: MapPin }].map(({ label, key, icon: Icon }) => (
                  <div key={key}><label className="text-[10px] text-gray-400 block mb-0.5">{label}</label><div className="flex items-center gap-1"><Icon size={12} className="text-gray-300 shrink-0" /><Input value={p[key] || ''} onChange={e => setP(prev => ({ ...prev, [key]: e.target.value }))} onBlur={() => save({ [key]: p[key] })} className="text-xs h-8" /></div></div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] text-gray-400 block mb-0.5">Montant devis (EUR)</label><Input type="number" value={p.devisAmount || ''} onChange={e => setP(prev => ({ ...prev, devisAmount: e.target.value }))} onBlur={() => save({ devisAmount: p.devisAmount })} className="text-xs h-8 font-mono" /></div>
                <div><label className="text-[10px] text-gray-400 block mb-0.5">Date relance</label><Input type="date" value={p.relanceDate || ''} onChange={e => { setP(prev => ({ ...prev, relanceDate: e.target.value })); save({ relanceDate: e.target.value }); }} className="text-xs h-8" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] text-gray-400 block mb-0.5">Priorite</label><select value={p.priorite || 'moyenne'} onChange={e => { setP(prev => ({ ...prev, priorite: e.target.value })); save({ priorite: e.target.value }); }} className="w-full border rounded-lg px-2 py-1.5 text-xs bg-white h-8">{Object.entries(PRIORITES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
                <div><label className="text-[10px] text-gray-400 block mb-0.5">Secteur</label><select value={p.secteur || 'entreprise'} onChange={e => { setP(prev => ({ ...prev, secteur: e.target.value })); save({ secteur: e.target.value }); setSectorScript(null); }} className="w-full border rounded-lg px-2 py-1.5 text-xs bg-white h-8">{Object.entries(SECTEURS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
              </div>
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div><p className="text-xs font-medium text-gray-700">Relance mensuelle auto</p><p className="text-[10px] text-gray-400">Rappel chaque mois</p></div>
                <button onClick={() => save({ relanceRecurrence: p.relanceRecurrence === 'mensuel' ? '' : 'mensuel' })} className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${p.relanceRecurrence === 'mensuel' ? 'bg-[#009893]' : 'bg-gray-300'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${p.relanceRecurrence === 'mensuel' ? 'translate-x-5' : 'translate-x-1'}`} /></button>
              </div>
              <Button onClick={handleDelete} variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 text-xs"><Trash2 size={12} className="mr-1" /> Supprimer</Button>
            </>
          )}

          {activeSection === 'comms' && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Communications</h3>
                <Button onClick={() => setShowCommForm(!showCommForm)} size="sm" className="bg-[#1B2A52] text-white text-xs h-7"><Plus size={12} className="mr-1" /> Nouvelle</Button>
              </div>
              {showCommForm && (
                <div className="bg-gray-50 rounded-xl p-3 space-y-2 border" data-testid="comm-form">
                  <div className="flex gap-1">
                    {COMM_TYPES.map(t => { const Icon = t.icon; return (
                      <button key={t.id} onClick={() => setCommForm(prev => ({ ...prev, type: t.id }))}
                        className={`flex-1 text-[10px] py-2 rounded-lg border flex flex-col items-center gap-0.5 ${commForm.type === t.id ? 'border-[#1B2A52] bg-[#1B2A52] text-white' : 'border-gray-200 text-gray-500'}`}>
                        <Icon size={12} />{t.label}</button>
                    ); })}
                  </div>
                  <Input value={commForm.sujet} onChange={e => setCommForm(prev => ({ ...prev, sujet: e.target.value }))} placeholder="Sujet (ex: Premier contact, Envoi devis...)" className="text-xs" data-testid="comm-sujet" />
                  <textarea value={commForm.contenu} onChange={e => setCommForm(prev => ({ ...prev, contenu: e.target.value }))} placeholder="Details de l'echange..." rows={2} className="w-full border rounded-lg px-3 py-2 text-xs resize-none" />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-400 block mb-0.5">Resultat</label>
                      <select value={commForm.resultat} onChange={e => setCommForm(prev => ({ ...prev, resultat: e.target.value }))} className="w-full border rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="comm-resultat">
                        <option value="">Choisir...</option>
                        {RESULTATS_APPEL.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 block mb-0.5">Montant estime (EUR)</label>
                      <Input type="number" value={commForm.montantEstime} onChange={e => setCommForm(prev => ({ ...prev, montantEstime: e.target.value }))} placeholder="0" className="text-xs h-8 font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-0.5">Prochaine action</label>
                    <select value={commForm.prochaineAction} onChange={e => setCommForm(prev => ({ ...prev, prochaineAction: e.target.value }))} className="w-full border rounded-lg px-2 py-1.5 text-xs bg-white">
                      {PROCHAINES_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>
                  {commForm.resultat && (
                    <div className="bg-blue-50 rounded-lg p-2 text-[10px] text-blue-600">
                      <Zap size={10} className="inline mr-1" />
                      Auto: {RESULTATS_APPEL.find(r => r.value === commForm.resultat)?.auto ? `Pipeline -> ${STATUTS[RESULTATS_APPEL.find(r => r.value === commForm.resultat).auto]?.label}` : 'Pas de changement auto'}
                      {commForm.montantEstime && ` | Devis: ${commForm.montantEstime}EUR`}
                      {commForm.prochaineAction && ` | Relance: ${PROCHAINES_ACTIONS.find(a => a.value === commForm.prochaineAction)?.label}`}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button onClick={addComm} size="sm" className="bg-[#009893] text-white text-xs flex-1" data-testid="comm-submit">Enregistrer</Button>
                    <Button onClick={() => setShowCommForm(false)} variant="outline" size="sm" className="text-xs">Annuler</Button>
                  </div>
                </div>
              )}
              {(p.communications || []).length === 0 && !showCommForm && <p className="text-xs text-gray-400 text-center py-8">Aucune communication</p>}
              {(p.communications || []).slice().reverse().map((c, i) => {
                const ct = COMM_TYPES.find(t => t.id === c.type) || COMM_TYPES[0]; const Icon = ct.icon;
                return (
                  <div key={i} className="bg-white rounded-lg border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-[#1B2A52]/10 flex items-center justify-center"><Icon size={12} className="text-[#1B2A52]" /></div>
                      <span className="text-xs font-bold text-gray-900">{ct.label}</span>
                      {c.resultat && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: (RESULTATS_APPEL.find(r => r.value === c.resultat)?.color || '#666') + '15', color: RESULTATS_APPEL.find(r => r.value === c.resultat)?.color || '#666' }}>{c.resultat}</span>}
                      <span className="text-[10px] text-gray-300 ml-auto">{c.date?.slice(0, 10)}</span>
                    </div>
                    <p className="text-xs text-gray-700 font-medium">{c.sujet}</p>
                    {c.contenu && <p className="text-[10px] text-gray-500 mt-0.5">{c.contenu}</p>}
                    {c.montantEstime && <p className="text-[10px] font-mono text-[#C9A84C] mt-1">Estime: {c.montantEstime}EUR</p>}
                    {c.prochaineAction && <p className="text-[10px] text-blue-500 mt-0.5">Action: {PROCHAINES_ACTIONS.find(a => a.value === c.prochaineAction)?.label || c.prochaineAction}</p>}
                  </div>
                );
              })}
            </>
          )}

          {activeSection === 'email' && (
            <>
              {!p.email && <div className="bg-red-50 rounded-lg p-3 text-xs text-red-600 flex items-center gap-2"><AlertTriangle size={14} /> Pas d'email renseigne pour ce prospect</div>}
              {!showEmailForm && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1.5">Type d'email</label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { id: 'premier_contact', label: 'Premier contact', icon: Zap },
                        { id: 'relance', label: 'Relance', icon: RefreshCw },
                        { id: 'offre_speciale', label: 'Offre speciale', icon: Flame },
                        { id: 'invitation', label: 'Invitation', icon: Star },
                        { id: 'remerciement', label: 'Remerciement', icon: Check },
                      ].map(t => {
                        const Icon = t.icon;
                        return (
                          <button key={t.id} onClick={() => setEmailType(t.id)}
                            className={`text-[10px] py-2 px-1.5 rounded-lg border text-center transition-all flex flex-col items-center gap-1 ${emailType === t.id ? 'border-[#1B2A52] bg-[#1B2A52] text-white' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                            data-testid={`email-type-${t.id}`}>
                            <Icon size={13} /><span>{t.label}</span>
                          </button>
                        );
                      })}
                      <button onClick={() => { setEmailForm({ subject: '', body: '', sender: 'restaurant' }); setShowEmailForm(true); }}
                        className="text-[10px] py-2 px-1.5 rounded-lg border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 flex flex-col items-center gap-1">
                        <Plus size={13} /><span>Vierge</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-0.5">Contexte / notes (optionnel)</label>
                    <Input value={emailContext} onChange={e => setEmailContext(e.target.value)}
                      placeholder="Ex: On s'est vu au salon, ils cherchent un traiteur pour juin..."
                      className="text-xs h-8" data-testid="email-context" />
                  </div>
                  <Button onClick={() => generateEmailIA(emailType)} disabled={generatingEmail}
                    className="w-full bg-gradient-to-r from-[#C9A84C] to-[#B08A3E] hover:from-[#B08A3E] hover:to-[#9A7A35] text-white h-10"
                    data-testid="generate-email-ia-btn">
                    {generatingEmail ? <><Loader2 size={14} className="animate-spin mr-2" /> L'IA redige votre email...</>
                      : <><Sparkles size={14} className="mr-2" /> Generer email IA personnalise</>}
                  </Button>
                  {offresRecommandees.length > 0 && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200">
                      <h4 className="text-[10px] text-amber-700 uppercase font-bold mb-1.5 flex items-center gap-1"><Flame size={11} /> Offres recommandees pour {(SECTEURS[p.secteur] || {}).label}</h4>
                      <div className="flex flex-wrap gap-1">
                        {offresRecommandees.map((o, i) => (
                          <span key={i} className="text-[10px] px-2 py-1 bg-white rounded-full border border-amber-200 text-amber-700">{o}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showEmailForm && (
                <div className="bg-gray-50 rounded-xl p-3 space-y-3 border" data-testid="email-compose">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-[#1B2A52]" />
                      <span className="text-xs font-bold text-gray-700">Email vers {p.email || '(pas d\'email)'}</span>
                      {siteScraped && <span className="text-[8px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">SITE ANALYSE</span>}
                    </div>
                    <Button onClick={() => generateEmailIA(emailType)} disabled={generatingEmail} variant="outline" size="sm" className="text-[10px] h-6 border-[#C9A84C] text-[#C9A84C]" data-testid="regenerate-email-btn">
                      {generatingEmail ? <Loader2 size={10} className="animate-spin" /> : <><RefreshCw size={10} className="mr-1" /> Regenerer</>}
                    </Button>
                  </div>
                  {emailVersions.length > 1 && (
                    <div>
                      <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1.5">Choisir le ton</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {emailVersions.map((v, i) => {
                          const tonConfig = { professionnel: { label: 'Formel', icon: Building, color: 'blue' }, decontracte: { label: 'Decontracte', icon: Smile, color: 'green' }, direct: { label: 'Direct', icon: Zap, color: 'orange' } };
                          const tc = tonConfig[v.ton] || { label: v.ton, icon: Mail, color: 'gray' };
                          const Icon = tc.icon;
                          return (
                            <button key={i} onClick={() => selectEmailVersion(i)}
                              className={`text-[10px] py-2 px-2 rounded-lg border transition-all flex items-center justify-center gap-1.5 ${selectedVersion === i
                                ? 'font-bold ring-1'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                              style={selectedVersion === i ? { borderColor: tc.color === 'blue' ? '#3b82f6' : tc.color === 'green' ? '#22c55e' : '#f97316', background: tc.color === 'blue' ? '#eff6ff' : tc.color === 'green' ? '#f0fdf4' : '#fff7ed' } : {}}
                              data-testid={`email-version-${v.ton}`}>
                              <Icon size={12} />{tc.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {saisonHook && (
                    <div className="bg-amber-50 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 border border-amber-200">
                      <Sun size={12} className="text-amber-500 shrink-0" />
                      <span className="text-[10px] text-amber-700">{saisonHook}</span>
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-0.5">Expediteur</label>
                    <select value={emailForm.sender} onChange={e => setEmailForm(prev => ({ ...prev, sender: e.target.value }))}
                      className="w-full border rounded-lg px-2 py-1.5 text-xs bg-white">
                      <option value="restaurant">Email Restaurant</option>
                      <option value="traiteur">Email Traiteur</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-0.5">Objet</label>
                    <Input value={emailForm.subject} onChange={e => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Objet de l'email..." className="text-xs" data-testid="email-subject" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-0.5">Contenu</label>
                    <textarea value={emailForm.body} onChange={e => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                      rows={10} className="w-full border rounded-lg px-3 py-2 text-xs resize-none leading-relaxed" data-testid="email-body"
                      placeholder="Bonjour..." />
                  </div>
                  {catalogue && (catalogue.formules || []).length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-2">
                      <p className="text-[10px] text-blue-500 font-bold mb-1">Inserer une formule :</p>
                      <div className="flex flex-wrap gap-1">
                        {(catalogue.formules || []).map((f, i) => (
                          <button key={i} onClick={() => setEmailForm(prev => ({ ...prev, body: prev.body + `\n- ${f.nom}: ${f.prix}EUR/pers. (${f.desc})` }))}
                            className="text-[9px] px-2 py-1 bg-white rounded border border-blue-200 text-blue-600 hover:bg-blue-100">
                            {f.nom} \u2014 {f.prix}EUR
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-[10px] text-gray-400 font-bold mb-1">Signature automatique :</p>
                    <div className="text-[10px] text-gray-500">
                      <p className="font-bold text-[#1B2A52]">{catalogue?.patron_nom || 'Laurent San Martin'}</p>
                      <p className="text-[#C9A84C] text-[9px] uppercase">{catalogue?.restaurant_nom || 'Le Mas'} \u2014 Restaurant & Traiteur</p>
                      <p>{catalogue?.patron_tel || '06 87 88 59 25'} | {catalogue?.restaurant_tel || '04 68 38 06 98'}</p>
                      <p>{catalogue?.restaurant_site || 'www.restaurant-le-mas.fr'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={sendEmail} disabled={sendingEmail || !p.email} className="flex-1 bg-[#009893] hover:bg-[#00807b] text-white text-xs" data-testid="send-email-btn">
                      {sendingEmail ? <><Loader2 size={14} className="animate-spin mr-1" /> Envoi...</> : <><Send size={14} className="mr-1" /> Envoyer</>}
                    </Button>
                    <Button onClick={() => { fetchScheduleSuggestion(); setShowSchedule(true); }} variant="outline" size="sm" className="text-xs border-amber-300 text-amber-600 hover:bg-amber-50" data-testid="schedule-email-btn">
                      <Clock size={12} className="mr-1" /> Programmer
                    </Button>
                    <Button onClick={() => copyText(emailForm.body)} variant="outline" size="sm" className="text-xs" data-testid="copy-email-btn">
                      <Copy size={12} />
                    </Button>
                    <Button onClick={() => setShowEmailForm(false)} variant="outline" size="sm" className="text-xs">Retour</Button>
                  </div>
                  {showSchedule && (
                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 space-y-2" data-testid="schedule-panel">
                      <h4 className="text-xs font-bold text-amber-700 flex items-center gap-1"><Clock size={12} /> Programmer l'envoi</h4>
                      {scheduleSuggestion ? (
                        <div className="bg-white rounded-lg p-2.5 border border-amber-100">
                          <p className="text-xs font-bold text-[#1B2A52]">{scheduleSuggestion.send_at_local}</p>
                          <p className="text-[10px] text-amber-600 mt-0.5">{scheduleSuggestion.raison}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">Horaire optimal pour le secteur {(SECTEURS[p.secteur] || {}).label}</p>
                        </div>
                      ) : <Loader2 size={14} className="animate-spin text-amber-500 mx-auto" />}
                      <div className="flex gap-2">
                        <Button onClick={scheduleEmail} disabled={scheduling || !scheduleSuggestion} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid="confirm-schedule-btn">
                          {scheduling ? <Loader2 size={12} className="animate-spin" /> : 'Programmer'}
                        </Button>
                        <Button onClick={() => setShowSchedule(false)} variant="outline" size="sm" className="text-xs">Annuler</Button>
                      </div>
                    </div>
                  )}
                  {!showSchedule && (
                    <Button onClick={createSequence} disabled={creatingSequence} variant="outline" className="w-full border-purple-200 text-purple-600 hover:bg-purple-50 text-xs h-8" data-testid="create-sequence-btn">
                      {creatingSequence ? <><Loader2 size={12} className="animate-spin mr-1" /> Sequence IA en cours...</>
                        : <><Zap size={12} className="mr-1" /> Creer sequence auto (3 emails IA)</>}
                    </Button>
                  )}
                  {scheduledEmails.length > 0 && (
                    <div className="bg-blue-50 rounded-xl p-2.5 border border-blue-200" data-testid="scheduled-list">
                      <h4 className="text-[10px] font-bold text-blue-600 uppercase mb-1.5 flex items-center gap-1"><Clock size={10} /> Emails programmes ({scheduledEmails.length})</h4>
                      <div className="space-y-1">
                        {scheduledEmails.map(se => (
                          <div key={se.id} className="flex items-center justify-between bg-white rounded-lg px-2 py-1.5 border border-blue-100">
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold text-gray-700 truncate">{se.subject}</p>
                              <p className="text-[9px] text-blue-500">{new Date(se.send_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}{se.sequence_step ? ` \u2014 Etape ${se.sequence_step}/3` : ''}</p>
                            </div>
                            <button onClick={() => cancelScheduled(se.id)} className="text-gray-300 hover:text-red-400 p-1" title="Annuler"><X size={12} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {(p.communications || []).filter(c => c.type === 'email').length > 0 && (
                <div className="mt-3">
                  <h4 className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Emails envoyes</h4>
                  {(p.communications || []).filter(c => c.type === 'email').slice().reverse().map((c, i) => (
                    <div key={i} className="bg-white rounded-lg border p-3 mb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <Send size={12} className="text-[#009893]" />
                        <span className="text-xs font-bold text-gray-900">{c.sujet}</span>
                        <span className="text-[10px] text-gray-300 ml-auto">{c.date?.slice(0, 10)}</span>
                      </div>
                      {c.destinataire && <p className="text-[10px] text-gray-400">A: {c.destinataire}</p>}
                      {c.contenu && <p className="text-[10px] text-gray-500 mt-1 line-clamp-3">{c.contenu}</p>}
                      {c.resultat && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 mt-1 inline-block">{c.resultat}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeSection === 'ia' && (
            <>
              {aiSuggestionLoading && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-xl border border-purple-100 animate-pulse">
                  <Loader2 size={14} className="animate-spin text-purple-400" />
                  <span className="text-xs text-purple-500">L'IA prepare une suggestion...</span>
                </div>
              )}
              {aiSuggestion && !aiSuggestionLoading && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-3 space-y-2" data-testid="ai-suggestion">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-purple-500" />
                      <span className="text-xs font-bold text-purple-700">Suggestion IA \u2014 {aiSuggestion.sequence}</span>
                      <span className="text-[9px] text-purple-400">({aiSuggestion.nb_contacts_precedents} contact{aiSuggestion.nb_contacts_precedents !== 1 ? 's' : ''} precedent{aiSuggestion.nb_contacts_precedents !== 1 ? 's' : ''})</span>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(aiSuggestion.message); toast.success('Copie !'); }}
                      className="text-xs text-purple-500 hover:text-purple-700 flex items-center gap-1">
                      <Copy size={10} /> Copier
                    </button>
                  </div>
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed bg-white/50 rounded-lg p-3">{aiSuggestion.message}</pre>
                  <div className="flex gap-2">
                    <button onClick={() => { setEmailForm({ subject: `Traiteur du Mas \u2014 ${p.nom}`, body: aiSuggestion.message, sender: 'restaurant' }); setShowEmailForm(true); }}
                      className="px-3 py-1 bg-purple-500 text-white text-[10px] rounded-lg flex items-center gap-1 hover:bg-purple-600">
                      <Mail size={10} /> Utiliser comme email
                    </button>
                    <button onClick={() => { setAiSuggestion(null); setAiSuggestionLoading(true); fetch(`${API}/api/crm/prospects/${p.id}/suggestion-ia`, { method: 'POST' }).then(r => r.json()).then(setAiSuggestion).catch(() => {}).finally(() => setAiSuggestionLoading(false)); }}
                      className="px-3 py-1 bg-white text-purple-500 text-[10px] rounded-lg border border-purple-200 flex items-center gap-1 hover:bg-purple-50">
                      <RefreshCw size={10} /> Regenerer
                    </button>
                  </div>
                </div>
              )}

              {!enrichment ? (
                <div className="text-center py-6">
                  <Sparkles size={28} className="mx-auto mb-2 text-[#C9A84C]/30" />
                  <p className="text-sm font-medium text-gray-600 mb-1">Enrichissement IA</p>
                  <p className="text-xs text-gray-400 mb-3">Score, analyse et suggestions personnalisees</p>
                  <Button onClick={handleEnrich} disabled={enriching} className="bg-gradient-to-r from-[#C9A84C] to-[#B08A3E] text-white">
                    {enriching ? <><Loader2 size={14} className="animate-spin mr-2" /> Analyse...</> : <><Sparkles size={14} className="mr-2" /> Lancer l'analyse</>}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-gradient-to-r from-[#C9A84C]/10 to-transparent rounded-xl p-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C9A84C] to-[#B08A3E] flex items-center justify-center shadow"><span className="text-lg font-bold text-white">{enrichment.scoreIA}</span></div>
                    <div><p className="text-xs font-bold text-gray-900">Score: {enrichment.scoreIA}/100</p><p className="text-[10px] text-gray-400">Potentiel conversion</p></div>
                    <Button onClick={handleEnrich} disabled={enriching} variant="outline" size="sm" className="ml-auto text-xs"><RefreshCw size={12} className={enriching ? 'animate-spin' : ''} /></Button>
                  </div>
                  <div className="bg-white rounded-xl border p-3"><h4 className="text-[10px] text-gray-400 uppercase mb-1">Analyse</h4><p className="text-xs text-gray-700 leading-relaxed">{enrichment.insights}</p></div>
                  <div className="bg-white rounded-xl border p-3">
                    <div className="flex items-center justify-between mb-1"><h4 className="text-[10px] text-gray-400 uppercase">Email suggere</h4><button onClick={() => copyText(enrichment.suggestion_email)} className="text-[10px] text-[#009893] flex items-center gap-0.5"><Copy size={10} /> Copier</button></div>
                    <pre className="text-[11px] text-gray-600 whitespace-pre-wrap font-sans leading-relaxed bg-gray-50 rounded-lg p-3">{enrichment.suggestion_email}</pre>
                  </div>
                </div>
              )}

              {loadingScript && <div className="flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-gray-300" /></div>}
              {sectorScript && (
                <div className="space-y-3 mt-2">
                  <div className="flex items-center gap-2"><BookOpen size={14} className="text-[#1B2A52]" /><h3 className="text-xs font-bold text-gray-700">Script d'appel \u2014 {(SECTEURS[p.secteur] || {}).label}</h3></div>
                  <div className="bg-[#1B2A52]/5 rounded-xl p-3 border border-[#1B2A52]/10">
                    <div className="flex items-center justify-between mb-1"><h4 className="text-[10px] text-[#1B2A52] uppercase font-bold">Ouverture</h4><button onClick={() => copyText(sectorScript.script_ouverture?.replace('{contact}', p.contact || '').replace('{nom}', p.nom || ''))} className="text-[10px] text-[#009893] flex items-center gap-0.5"><Copy size={10} /> Copier</button></div>
                    <p className="text-xs text-gray-700 leading-relaxed italic">&ldquo;{sectorScript.script_ouverture?.replace('{contact}', p.contact || 'M./Mme').replace('{nom}', p.nom || "l'entreprise")}&rdquo;</p>
                  </div>
                  <div className="bg-white rounded-xl border p-3">
                    <h4 className="text-[10px] text-gray-400 uppercase mb-2 font-bold">Arguments cles</h4>
                    <div className="space-y-1">
                      {(sectorScript.arguments || []).map((a, i) => <div key={i} className="flex items-start gap-2 text-xs text-gray-700"><Check size={12} className="text-green-500 mt-0.5 shrink-0" /><span>{a}</span></div>)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border p-3">
                    <h4 className="text-[10px] text-gray-400 uppercase mb-2 font-bold">Prestations</h4>
                    <div className="flex flex-wrap gap-1">
                      {(sectorScript.prestations || []).map((pr, i) => <span key={i} className="text-[10px] px-2 py-1 bg-gray-50 rounded-full text-gray-600">{pr}</span>)}
                    </div>
                  </div>
                  {Object.keys(sectorScript.objections || {}).length > 0 && (
                    <div className="bg-white rounded-xl border p-3">
                      <h4 className="text-[10px] text-gray-400 uppercase mb-2 font-bold">Objections & Reponses</h4>
                      <div className="space-y-2">
                        {Object.entries(sectorScript.objections).map(([obj, rep]) => (
                          <div key={obj}>
                            <p className="text-[10px] font-bold text-red-500">&#x26A0; &ldquo;{obj}&rdquo;</p>
                            <p className="text-xs text-gray-700 mt-0.5 pl-3 border-l-2 border-green-300">{rep}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[10px] text-purple-700 uppercase font-bold flex items-center gap-1"><Sparkles size={11} /> Objections IA personnalisees</h4>
                      <Button onClick={async () => {
                        setEnriching(true);
                        try {
                          const res = await fetch(`${API}/api/crm/prospects/${p.id}/generate-objections`, { method: 'POST' });
                          const data = await res.json();
                          if (res.ok && data.objections) {
                            setEnrichment(prev => ({ ...prev, ia_objections: data.objections, ia_conseil: data.conseil_approche }));
                            toast.success('Objections IA generees');
                          } else { toast.error(data.detail || 'Erreur IA'); }
                        } catch { toast.error('Erreur'); }
                        setEnriching(false);
                      }} disabled={enriching} variant="outline" size="sm" className="text-xs border-purple-300 text-purple-600" data-testid="generate-objections-btn">
                        {enriching ? <Loader2 size={11} className="animate-spin mr-1" /> : <Sparkles size={11} className="mr-1" />} Generer
                      </Button>
                    </div>
                    {(enrichment?.ia_conseil || p.ia_conseil_approche) && (
                      <p className="text-xs text-purple-700 italic bg-white/50 rounded-lg px-3 py-1.5 mb-2">{enrichment?.ia_conseil || p.ia_conseil_approche}</p>
                    )}
                    {(enrichment?.ia_objections || p.ia_objections || []).length > 0 ? (
                      <div className="space-y-2">
                        {(enrichment?.ia_objections || p.ia_objections).map((o, i) => (
                          <div key={i}>
                            <p className="text-[10px] font-bold text-red-500">&#x26A0; &ldquo;{o.objection}&rdquo;</p>
                            <p className="text-xs text-gray-700 mt-0.5 pl-3 border-l-2 border-purple-300">{o.reponse}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-purple-400 text-center py-2">Cliquez "Generer" pour des objections personnalisees a ce prospect</p>
                    )}
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                    <div className="flex items-center justify-between mb-1"><h4 className="text-[10px] text-green-700 uppercase font-bold">Closing</h4><button onClick={() => copyText(sectorScript.script_closing)} className="text-[10px] text-[#009893] flex items-center gap-0.5"><Copy size={10} /> Copier</button></div>
                    <p className="text-xs text-green-800 leading-relaxed italic">&ldquo;{sectorScript.script_closing}&rdquo;</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-white rounded-lg border p-2"><p className="text-[10px] text-gray-400 uppercase">CA moyen</p><p className="text-sm font-bold text-[#C9A84C]">{sectorScript.ca_moyen?.toLocaleString('fr-FR')}EUR</p></div>
                    <div className="bg-white rounded-lg border p-2"><p className="text-[10px] text-gray-400 uppercase">Saisonnalite</p><p className="text-[10px] text-gray-600 leading-tight">{sectorScript.saisonnalite}</p></div>
                  </div>
                  {(sectorScript.naf_codes || []).length > 0 && (
                    <div className="bg-white rounded-xl border p-3">
                      <h4 className="text-[10px] text-gray-400 uppercase mb-1 font-bold">Codes NAF INSEE</h4>
                      <div className="flex flex-wrap gap-1">
                        {sectorScript.naf_codes.map((c, i) => <span key={i} className="text-[10px] font-mono px-2 py-0.5 bg-blue-50 text-blue-600 rounded">{c}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeSection === 'notes' && (
            <>
              <div className="flex gap-2"><Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Ajouter une note..." className="text-xs flex-1" data-testid="note-input" onKeyDown={e => e.key === 'Enter' && addNote()} /><Button onClick={addNote} size="sm" className="bg-[#1B2A52]"><Plus size={14} /></Button></div>
              {(p.notes || []).length === 0 && <p className="text-xs text-gray-400 text-center py-8">Aucune note</p>}
              {(p.notes || []).slice().reverse().map((n, i) => <div key={i} className="text-xs bg-gray-50 rounded-lg px-3 py-2"><p className="text-gray-700">{n.text}</p><p className="text-[10px] text-gray-300 mt-0.5">{n.date?.slice(0, 10)}</p></div>)}
            </>
          )}

          {activeSection === 'history' && (
            <>
              {(p.historique || []).length === 0 && <p className="text-xs text-gray-400 text-center py-8">Aucun historique</p>}
              {(p.historique || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${h.type === 'statut' ? 'bg-blue-400' : h.type === 'creation' ? 'bg-green-400' : h.type === 'communication' ? 'bg-purple-400' : h.type === 'enrichissement' ? 'bg-yellow-400' : 'bg-gray-300'}`} />
                  <div><p className="text-xs text-gray-600">{h.action}</p><p className="text-[10px] text-gray-300">{h.date?.slice(0, 10)}</p></div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
